import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { tickerAPI, aiAPI } from '../../services/api';
import { resolveShortcodes } from '../../utils/tickerUtils';

const AI_MODES = [
    { value: 'off', label: 'KI aus' },
    { value: 'spell', label: 'Rechtschreibung' },
    { value: 'full', label: 'Volle KI' },
];
const AI_MODE_KEY = 'ticker_ai_mode';

const EVENT_TYPES = [
    { value: 1, label: 'Info', icon: 'ℹ️' },
    { value: 8, label: 'Chance', icon: '💥' },
    { value: 9, label: 'Phase', icon: '⏱' },
    { value: 10, label: 'Stimmung', icon: '🎉' },
];

const TEMPLATES = [
    { label: 'Ecke Heim', text: 'Ecke für #h' },
    { label: 'Ecke Gast', text: 'Ecke für #a' },
    { label: 'Verletzung', text: 'Behandlungspause auf dem Platz.' },
    { label: 'VAR', text: 'VAR-Prüfung läuft.' },
    { label: 'Zuschauer', text: 'Zuschauer:' },
];

const TickerInput = forwardRef(function TickerInput({
    gameId,
    currentMinute,
    half,
    game,
    lineup,
    availablePlayers,
    onCreated,
    gameFinished,
    onUndoRegister,
}, ref) {
    const textareaRef = useRef(null);
    const [text, setText] = useState('');
    const [type, setType] = useState(1);
    const [minute, setMinute] = useState(currentMinute || 0);
    const [minuteManual, setMinuteManual] = useState(false);
    const [forReport, setForReport] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [aiLoading, setAiLoading] = useState(null);
    const [error, setError] = useState('');
    const [aiMode, setAiMode] = useState(() => localStorage.getItem(AI_MODE_KEY) || 'spell');
    const [showAiSettings, setShowAiSettings] = useState(false);

    useImperativeHandle(ref, () => ({
        focus: () => textareaRef.current?.focus(),
        setChanceType: () => setType(8),
    }));

    const changeAiMode = (mode) => {
        setAiMode(mode);
        localStorage.setItem(AI_MODE_KEY, mode);
    };

    useEffect(() => {
        if (gameFinished) {
            setMinute(0);
            return;
        }
        if (!minuteManual) setMinute(currentMinute || 0);
    }, [currentMinute, minuteManual, gameFinished]);

    const preview = useMemo(() => {
        if (!text.includes('#')) return null;
        const { textResolved } = resolveShortcodes(text, game, availablePlayers, lineup);
        return textResolved !== text ? textResolved : null;
    }, [text, game, availablePlayers, lineup]);

    const handleAI = async (action) => {
        if (!text.trim()) return;
        setAiLoading(action);
        setError('');
        try {
            if (action === 'improve') {
                const res = await aiAPI.improveText(text);
                setText(res.data.result);
            } else if (action === 'spell') {
                const res = await aiAPI.spellcheck(text);
                setText(res.data.result);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setAiLoading(null);
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!text.trim() || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            const { textResolved, mentions } = resolveShortcodes(text, game, availablePlayers, lineup);
            const res = await tickerAPI.create({
                gid: gameId,
                type,
                minute: gameFinished ? 0 : (parseInt(minute, 10) || 0),
                half: gameFinished ? 0 : (half || 1),
                textRaw: text,
                textResolved,
                textPublished: textResolved,
                mentionedPlayers: mentions,
                published: true,
                reportFlags: { useInReport: forReport, isKeyScene: false, category: '' },
            });
            onCreated(res.data);
            onUndoRegister?.(res.data, 'Meldung');
            setText('');
            setType(1);
            setForReport(false);
            setMinuteManual(false);
            textareaRef.current?.focus();
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Speichern');
        } finally {
            setSubmitting(false);
        }
    };

    const applyTemplate = (templateText) => {
        setText(templateText);
        textareaRef.current?.focus();
    };

    return (
        <form onSubmit={handleSubmit} className={`ticker-composer ${gameFinished ? 'ticker-composer--postmatch' : ''}`}>
            <div className="ticker-composer-types">
                {EVENT_TYPES.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => setType(t.value)}
                        className={`ticker-type-chip ${type === t.value ? 'ticker-type-chip--active' : ''}`}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            <div className="ticker-template-bar">
                {TEMPLATES.map((tpl) => (
                    <button
                        key={tpl.label}
                        type="button"
                        className="ticker-template-chip"
                        onClick={() => applyTemplate(tpl.text)}
                    >
                        {tpl.label}
                    </button>
                ))}
            </div>

            {preview && (
                <div className="ticker-preview">
                    <span className="opacity-60 mr-1">→</span>{preview}
                </div>
            )}

            <div className="ticker-composer-body">
                {!gameFinished ? (
                    <input
                        type="number"
                        min="0"
                        max="120"
                        value={minute}
                        onChange={(e) => { setMinuteManual(true); setMinute(e.target.value); }}
                        onBlur={() => setMinuteManual(false)}
                        className="ticker-minute-input"
                        title="Spielminute"
                        aria-label="Minute"
                    />
                ) : (
                    <div className="ticker-minute-postmatch" title="Spiel beendet">NS</div>
                )}
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={gameFinished ? 'Nachtrag eingeben…' : 'Meldung tippen… Enter = senden'}
                    className="ticker-textarea"
                    rows={2}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                    }}
                />
                <button type="submit" disabled={submitting || !text.trim()} className="ticker-post-btn">
                    {submitting ? '…' : 'Senden'}
                </button>
            </div>

            <div className="ticker-composer-footer">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400">
                    <input
                        type="checkbox"
                        checked={forReport}
                        onChange={(e) => setForReport(e.target.checked)}
                        className="checkbox checkbox-xs checkbox-primary"
                    />
                    Für Bericht
                </label>

                <button
                    type="button"
                    className="ticker-ai-toggle"
                    onClick={() => setShowAiSettings((v) => !v)}
                >
                    ✨ KI {showAiSettings ? '▲' : '▼'}
                </button>

                {error && <span className="text-xs text-red-400 ml-auto">{error}</span>}
            </div>

            {(showAiSettings || gameFinished) && (
                <div className="ticker-composer-ai">
                    <div className="ticker-ai-segment">
                        {AI_MODES.map((m) => (
                            <button
                                key={m.value}
                                type="button"
                                className={aiMode === m.value ? 'active' : ''}
                                onClick={() => changeAiMode(m.value)}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                    {aiMode !== 'off' && text.trim().length > 5 && (
                        <div className="flex gap-2 mt-2">
                            <button type="button" onClick={() => handleAI('spell')} disabled={!!aiLoading} className="ticker-ai-action-btn">
                                {aiLoading === 'spell' ? '…' : '🔤 Rechtschreibung'}
                            </button>
                            {aiMode === 'full' && (
                                <button type="button" onClick={() => handleAI('improve')} disabled={!!aiLoading} className="ticker-ai-action-btn ticker-ai-action-btn--improve">
                                    {aiLoading === 'improve' ? '…' : '✨ Verbessern'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </form>
    );
});

export default TickerInput;
